/**
 * @file FooterColumnTitle.tsx
 * @description Заголовок колонки подвала, который правится на месте
 * (phase19.16, приёмка Р2а; SB113; uiux-footer-columns §4.6, §5): поле в
 * шапке карточки, сохранение при уходе с поля (или Enter), Esc — вернуть,
 * счётчик «N/40», подсказка про перенос длинного заголовка. Пусто — колонка
 * без заголовка; у автосписка категорий заголовок обязателен.
 *
 * Своего «копирования» значения в эффекте нет (паттерн `{ key, data }`):
 * пока человек правит, в поле его текст — свежее меню с сервера его не
 * затирает; после ухода с поля текст держится, пока сервер не вернёт новый
 * заголовок (`key` — значение до правки), при ошибке — прежнее значение.
 */

import { useState } from "react";

import { Input } from "../../ui/input";
import { FOOTER_COLUMN_TITLE_MAX, FOOTER_COLUMN_TITLE_SOFT } from "../../../lib/site-view/footer-columns";

interface Props {
  /** id для связи с подписью. */
  id: string;
  value: string | null;
  /** Заголовок обязателен (автосписок категорий). */
  required: boolean;
  disabled?: boolean;
  /** Сохранить; `false` — не сохранилось (текст вернётся к значению с сервера). */
  onSave: (label: string | null) => Promise<boolean>;
}

/** Правка: `key` — значение с сервера на момент начала, `data` — текст в поле. */
interface Draft {
  key: string | null;
  data: string;
  editing: boolean;
}

export function FooterColumnTitle({ id, value, required, disabled = false, onSave }: Props) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  // В фокусе — всегда текст человека; после ухода — пока сервер не ответил новым значением.
  const live = draft !== null && (draft.editing || draft.key === value);
  const text = live ? draft.data : (value ?? "");

  async function commit() {
    const next = text.trim();
    if (required && next === "") {
      setError("У списка категорий нужен заголовок.");
      setDraft(null);
      return;
    }
    setError(null);
    if (next === (value ?? "")) {
      setDraft(null);
      return;
    }
    setDraft({ key: value, data: next, editing: false });
    if (!(await onSave(next === "" ? null : next))) setDraft(null);
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="type-caption text-on-surface-variant">
        Заголовок
      </label>
      <Input
        id={id}
        value={text}
        placeholder="Без заголовка"
        maxLength={FOOTER_COLUMN_TITLE_MAX}
        disabled={disabled}
        aria-invalid={error !== null}
        onFocus={() => setDraft({ key: value, data: text, editing: true })}
        onChange={(e) => setDraft({ key: draft?.key ?? value, data: e.target.value, editing: true })}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft({ key: value, data: value ?? "", editing: true });
            setError(null);
          }
        }}
      />
      <span className="type-caption text-on-surface-variant tabular-nums">
        {text.length}/{FOOTER_COLUMN_TITLE_MAX}
        {text.length > FOOTER_COLUMN_TITLE_SOFT ? " · длинный заголовок перенесётся на 2 строки — соседние колонки выровняются по нему" : ""}
      </span>
      {error ? <p className="type-caption text-destructive">{error}</p> : null}
    </div>
  );
}
